import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { 
  Upload, CheckCircle, Play, Lock, Unlock, 
  Video, Clock, ChevronRight, Loader2, 
  FileText, Award, Trophy, ExternalLink, Crown, Zap,
  FileCheck, AlertCircle, User, BookOpen
} from 'lucide-react';
import api from '../services/api';
import { toast } from 'sonner';
import AnnouncementTicker from '../components/AnnouncementTicker';
import DailyRoadmap from '../components/DailyRoadmap';
import NationalScoutRanking from '../components/NationalScoutRanking';
import SportMajorSelection from '../components/SportMajorSelection';

const HighPerformanceDashboard = () => {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [dashboardData, setDashboardData] = useState(null);
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [transcriptLoading, setTranscriptLoading] = useState(false);
  const [showDirectorModal, setShowDirectorModal] = useState(false);
  const [selectedSportMajor, setSelectedSportMajor] = useState(null);

  const handleSelectSportMajor = async (sportId) => {
    setSelectedSportMajor(sportId);
    // Optionally save to backend
    try {
      const token = localStorage.getItem('academy_token');
      await api.post('/academy/user/sport-major', { sport_major: sportId }, {
        headers: { Authorization: `Bearer ${token}` }
      });
    } catch (error) {
      console.log('Sport major update pending backend implementation');
    }
  };

  useEffect(() => {
    loadDashboard();
  }, []);

  const loadDashboard = async () => {
    const token = localStorage.getItem('academy_token');
    if (!token) {
      toast.error('Please log in');
      navigate('/academy/enroll');
      return;
    }

    try {
      const response = await api.get('/academy/high-performance/dashboard', {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success) {
        setDashboardData(response.data);
        if (response.data.coach_corner?.featured_video) {
          setSelectedVideo(response.data.coach_corner.featured_video);
        }
      }
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error('Please complete enrollment');
        navigate('/academy/enroll');
      } else if (error.response?.status === 401) {
        localStorage.removeItem('academy_token');
        navigate('/academy/enroll');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.size > 10 * 1024 * 1024) {
      toast.error('File size must be less than 10MB');
      return;
    }

    setUploading(true);
    try {
      const reader = new FileReader();
      reader.onload = async (e) => {
        const token = localStorage.getItem('academy_token');
        const response = await api.post('/academy/high-performance/commitment/upload', {
          file_data: e.target.result,
          file_name: file.name,
          file_type: file.type
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });

        if (response.data.success) {
          toast.success('Commitment letter uploaded! Week 1 is now unlocked.');
          loadDashboard();
        }
        setUploading(false);
      };
      reader.readAsDataURL(file);
    } catch (error) {
      toast.error('Failed to upload file');
      setUploading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black text-white pt-16 flex items-center justify-center">
        <Loader2 className="w-12 h-12 animate-spin text-orange-500" />
      </div>
    );
  }

  const { student, commitment_box, week1_unlocked, coach_corner } = dashboardData || {};
  
  const isEliteTier = student?.subscription_tier === 'elite_pivot' || student?.subscription_tier === 'elite';

  const handleRequestTranscript = async () => {
    if (!isEliteTier) {
      setShowUpgradeModal(true);
      return;
    }
    
    setTranscriptLoading(true);
    try {
      const token = localStorage.getItem('academy_token');
      const response = await api.post('/academy/agency/request', {
        service_id: 'transcript',
        service_title: 'On-Demand Scholastic Transcript'
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        toast.success('Transcript request submitted! Your coach will prepare it within 48 hours.');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to request transcript');
    } finally {
      setTranscriptLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-black text-white pt-24">
      {/* Announcement Ticker */}
      <AnnouncementTicker />
      
      {/* 30-DAY SPRINT STATUS HEADER */}
      <div className="container mx-auto px-6 py-4">
        <Card className="bg-gradient-to-r from-orange-500/10 via-amber-500/5 to-orange-500/10 border-2 border-orange-500/30 mb-6">
          <CardContent className="p-4">
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 bg-orange-500/20 rounded-xl flex items-center justify-center">
                  <Zap className="w-7 h-7 text-orange-500" />
                </div>
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-3xl font-black text-orange-500">DAY {student?.sprint_day || 1}</span>
                    <span className="text-gray-500 text-lg">of 30</span>
                  </div>
                  <p className="text-gray-400 text-sm">
                    Sprint Status: <span className={`font-bold ${student?.sprint_status === 'On Track' ? 'text-green-500' : student?.sprint_status === 'Behind' ? 'text-red-500' : 'text-yellow-500'}`}>
                      {student?.sprint_status || 'On Track'}
                    </span>
                  </p>
                </div>
              </div>
              
              <div className="flex-1 min-w-[200px] max-w-[400px]">
                <div className="flex items-center justify-between text-xs text-gray-500 mb-1">
                  <span>Progress</span>
                  <span>{Math.round(((student?.sprint_day || 1) / 30) * 100)}%</span>
                </div>
                <div className="h-2 bg-gray-800 rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-gradient-to-r from-orange-500 to-amber-500 rounded-full transition-all duration-500"
                    style={{ width: `${((student?.sprint_day || 1) / 30) * 100}%` }}
                  />
                </div>
              </div>
              
              <div className="text-right">
                <p className="text-xs text-gray-500 uppercase tracking-wider">Next Milestone</p>
                <p className="text-white font-bold">{student?.next_milestone || 'IXL Math Certification'}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
      
      {/* Header with Orange/Black Wave Motion Graphics */}
      <div className="relative overflow-hidden border-b border-gray-900">
        {/* Wave Graphics Background */}
        <div className="absolute inset-0 opacity-30">
          <div className="absolute inset-0 bg-gradient-to-r from-orange-600/20 via-transparent to-orange-500/20"></div>
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-orange-500/10 via-transparent to-transparent"></div>
        </div>
        
        <div className="container mx-auto px-6 py-8 relative z-10">
          <div className="flex items-center gap-4 mb-2">
            <div className="w-12 h-12 bg-orange-500/20 rounded-lg flex items-center justify-center border border-orange-500/30">
              <Trophy className="w-6 h-6 text-orange-500" />
            </div>
            <div>
              <h1 className="text-2xl md:text-3xl font-black tracking-tight">
                HIGH-PERFORMANCE <span className="text-orange-500">DASHBOARD</span>
              </h1>
              <p className="text-gray-500 text-sm uppercase tracking-widest">Scholar Athlete Command Center</p>
            </div>
          </div>
          
          {/* Student Info Bar */}
          <div className="flex flex-wrap items-center gap-4 mt-4 pt-4 border-t border-gray-800">
            <div className="flex items-center gap-2">
              <span className="text-gray-500 text-xs uppercase tracking-wider">Athlete:</span>
              <span className="text-white font-bold">{student?.name}</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="px-3 py-1 bg-orange-500/20 text-orange-500 rounded font-black text-xs uppercase tracking-wider border border-orange-500/30">
                {student?.subscription_tier?.replace('_', ' ').toUpperCase()}
              </span>
            </div>
            <div className="flex items-center gap-2 text-gray-500 text-xs">
              <span>Signed:</span>
              <span className="text-gray-400">{student?.enrolled_at ? new Date(student.enrolled_at).toLocaleDateString() : ''}</span>
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-6 py-8">
        {/* Academic Power Score + Quick Start Section */}
        <div className="mb-8 grid md:grid-cols-2 gap-6">
          {/* Academic Power Score Display */}
          <div className="bg-gradient-to-br from-orange-500/20 to-amber-500/10 rounded-xl border-2 border-orange-500/50 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-orange-500 text-xs font-bold uppercase tracking-widest mb-1">Academic Power Score</p>
                <p className="text-6xl font-black text-white">{student?.academic_power_score || 0}</p>
                <p className="text-gray-500 text-sm mt-1">Your competitive edge metric</p>
              </div>
              <div className="w-20 h-20 rounded-full bg-orange-500/20 flex items-center justify-center border-2 border-orange-500">
                <Trophy className="w-10 h-10 text-orange-500" />
              </div>
            </div>
          </div>
          
          {/* Quick Start - The Combine */}
          <div 
            onClick={() => navigate('/academy/combine')}
            className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-xl border-2 border-gray-700 hover:border-orange-500 p-6 cursor-pointer transition-all group"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-400 text-xs font-bold uppercase tracking-widest mb-1">Daily Rep</p>
                <p className="text-2xl font-black text-white group-hover:text-orange-500 transition-colors">THE COMBINE</p>
                <p className="text-gray-500 text-sm mt-1">Logic Engine Assessment</p>
              </div>
              <Button className="bg-orange-500 hover:bg-orange-600 text-black font-bold" data-testid="quick-start-combine-btn">
                START →
              </Button>
            </div>
            <div className="mt-4 flex gap-2">
              <span className="px-2 py-1 bg-orange-500/10 text-orange-500 rounded text-xs">Math</span>
              <span className="px-2 py-1 bg-blue-500/10 text-blue-500 rounded text-xs">Literacy</span>
              <span className="px-2 py-1 bg-purple-500/10 text-purple-500 rounded text-xs">Strategy</span>
              <span className="px-2 py-1 bg-yellow-500/10 text-yellow-500 rounded text-xs">Identity</span>
            </div>
          </div>
        </div>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Left Column: Commitment Box + Module Status */}
          <div className="lg:col-span-1 space-y-6">
            {/* The Commitment Box */}
            <div className={`relative overflow-hidden rounded-lg border-2 transition-all ${
              commitment_box?.uploaded 
                ? 'border-green-500 bg-green-500/5' 
                : 'border-orange-500 bg-orange-500/5'
            }`}>
              {/* Grid Background Pattern */}
              <div className="absolute inset-0 opacity-10">
                <div className="w-full h-full" style={{
                  backgroundImage: 'linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)',
                  backgroundSize: '20px 20px'
                }}></div>
              </div>
              
              <div className="relative p-6">
                <div className="flex items-center gap-3 mb-4">
                  <FileText className={`w-5 h-5 ${commitment_box?.uploaded ? 'text-green-500' : 'text-orange-500'}`} />
                  <h3 className="text-white font-black text-lg uppercase tracking-wider">The Commitment Box</h3>
                </div>
                
                {commitment_box?.uploaded ? (
                  <div className="text-center py-6">
                    <div className="w-16 h-16 rounded-full bg-green-500/20 flex items-center justify-center mx-auto mb-4 border-2 border-green-500">
                      <CheckCircle className="w-8 h-8 text-green-500" />
                    </div>
                    <p className="text-green-500 font-black text-lg uppercase tracking-wider mb-2">Commitment Verified</p>
                    <p className="text-gray-400 text-sm mb-4">{commitment_box.message}</p>
                    {commitment_box.url && (
                      <a 
                        href={commitment_box.url} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 text-sm text-orange-500 hover:text-orange-400 transition-colors"
                      >
                        <ExternalLink className="w-4 h-4" />
                        View Document
                      </a>
                    )}
                  </div>
                ) : (
                  <div className="text-center py-4">
                    {/* Download Director-Signed LOI Button */}
                    <a
                      href="/director-signed-loi.pdf"
                      download="PRSVR-Director-Signed-LOI.pdf"
                      className="inline-flex items-center gap-2 bg-orange-500 hover:bg-orange-600 text-white font-black px-6 py-3 rounded-lg mb-6 transition-all shadow-lg shadow-orange-500/20 hover:shadow-orange-500/40"
                      data-testid="download-director-loi-btn"
                    >
                      <FileText className="w-5 h-5" />
                      DOWNLOAD DIRECTOR-SIGNED LOI
                    </a>
                    <p className="text-gray-400 text-sm mb-6">
                      Download, sign, and re-upload to confirm your commitment
                    </p>
                    
                    <div 
                      onClick={() => fileInputRef.current?.click()}
                      className="border-2 border-dashed border-orange-500/50 rounded-lg p-8 cursor-pointer hover:border-orange-500 hover:bg-orange-500/10 transition-all"
                    >
                      {uploading ? (
                        <Loader2 className="w-12 h-12 animate-spin text-orange-500 mx-auto" />
                      ) : (
                        <>
                          <Upload className="w-12 h-12 text-orange-500 mx-auto mb-4" />
                          <p className="text-white font-bold mb-2">Upload Your Signed Commitment</p>
                          <p className="text-gray-500 text-sm">
                            Photo or screenshot of your signed Letter of Intent
                          </p>
                          <p className="text-gray-600 text-xs mt-2">PNG, JPG, PDF up to 10MB</p>
                        </>
                      )}
                    </div>
                    <p className="text-orange-500 text-sm mt-4 font-medium">
                      {commitment_box?.message}
                    </p>
                  </div>
                )}
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*,.pdf"
                  onChange={handleFileSelect}
                  className="hidden"
                />
              </div>
            </div>

            {/* Module 01 Status - Visual Gating */}
            <div className={`relative overflow-hidden rounded-lg transition-all ${
              week1_unlocked 
                ? 'border-2 border-orange-500 bg-gradient-to-br from-orange-500/10 to-orange-600/5' 
                : 'border border-gray-800 bg-gray-900/50'
            }`}>
              {/* Grid Background for unlocked */}
              {week1_unlocked && (
                <div className="absolute inset-0 opacity-10">
                  <div className="w-full h-full" style={{
                    backgroundImage: 'linear-gradient(rgba(249,115,22,0.3) 1px, transparent 1px), linear-gradient(90deg, rgba(249,115,22,0.3) 1px, transparent 1px)',
                    backgroundSize: '20px 20px'
                  }}></div>
                </div>
              )}
              
              <div className="relative p-6">
                <div className="flex items-center gap-4">
                  <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${
                    week1_unlocked 
                      ? 'bg-orange-500/20 border-2 border-orange-500' 
                      : 'bg-gray-800 border border-gray-700'
                  }`}>
                    {week1_unlocked ? (
                      <Unlock className="w-6 h-6 text-orange-500" />
                    ) : (
                      <Lock className="w-6 h-6 text-gray-600" />
                    )}
                  </div>
                  <div className="flex-1">
                    <h3 className={`font-black uppercase tracking-wider ${
                      week1_unlocked ? 'text-orange-500' : 'text-gray-500'
                    }`}>
                      Module 01: The Performance Audit
                    </h3>
                    <p className={`text-sm ${week1_unlocked ? 'text-gray-400' : 'text-gray-600'}`}>
                      {week1_unlocked 
                        ? 'Active — Begin your training' 
                        : 'Locked — Upload commitment to unlock'}
                    </p>
                  </div>
                </div>
                
                {week1_unlocked && (
                  <Button
                    onClick={() => navigate('/academy/lms')}
                    className="w-full mt-4 bg-orange-500 hover:bg-orange-600 text-black font-black uppercase tracking-wider"
                  >
                    Enter The Combine <ChevronRight className="w-4 h-4 ml-2" />
                  </Button>
                )}
              </div>
            </div>

            {/* Quick Actions */}
            <div className="border border-gray-800 rounded-lg bg-gray-900/30">
              <div className="p-4 border-b border-gray-800">
                <h3 className="text-white font-black uppercase tracking-wider text-sm">Quick Actions</h3>
              </div>
              <div className="p-4 space-y-3">
                <Button
                  onClick={() => navigate('/academy/combine')}
                  variant="outline"
                  className="w-full justify-start border-gray-700 text-gray-300 hover:bg-orange-500/10 hover:border-orange-500/50 hover:text-orange-500 transition-all"
                >
                  <Award className="w-4 h-4 mr-3" />
                  The Combine
                </Button>
                <Button
                  onClick={() => navigate('/academy/grandmaster')}
                  variant="outline"
                  className="w-full justify-start border-gray-700 text-gray-300 hover:bg-orange-500/10 hover:border-orange-500/50 hover:text-orange-500 transition-all"
                >
                  <Zap className="w-4 h-4 mr-3" />
                  30-Day Sprint
                </Button>
                <Button
                  onClick={() => navigate('/academy/vault')}
                  variant="outline"
                  className="w-full justify-start border-gray-700 text-gray-300 hover:bg-orange-500/10 hover:border-orange-500/50 hover:text-orange-500 transition-all"
                >
                  <Lock className="w-4 h-4 mr-3" />
                  The Vault
                </Button>
                
                {/* Request Transcript - Elite Pivot Feature */}
                <Button
                  onClick={handleRequestTranscript}
                  disabled={transcriptLoading}
                  variant="outline"
                  className={`w-full justify-start transition-all ${
                    isEliteTier 
                      ? 'border-amber-500/50 text-amber-400 hover:bg-amber-500/10 hover:border-amber-500' 
                      : 'border-gray-700 text-gray-500 hover:bg-gray-800'
                  }`}
                >
                  {transcriptLoading ? (
                    <Loader2 className="w-4 h-4 mr-3 animate-spin" />
                  ) : isEliteTier ? (
                    <FileCheck className="w-4 h-4 mr-3" />
                  ) : (
                    <Lock className="w-4 h-4 mr-3" />
                  )}
                  Request Transcript
                  {!isEliteTier && (
                    <Crown className="w-3 h-3 ml-auto text-amber-500" />
                  )}
                </Button>
              </div>
            </div>
          </div>

          {/* Right Column: Coach's Corner */}
          <div className="lg:col-span-2">
            <div className="border border-gray-800 rounded-lg bg-gray-900/30 overflow-hidden">
              {/* Header */}
              <div className="p-4 border-b border-gray-800 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 bg-orange-500/20 rounded flex items-center justify-center">
                    <Video className="w-4 h-4 text-orange-500" />
                  </div>
                  <h3 className="text-white font-black uppercase tracking-wider">Coach's Corner</h3>
                </div>
                
                {/* MEET THE DIRECTOR Button */}
                <Button
                  onClick={() => setShowDirectorModal(true)}
                  className="bg-orange-500 hover:bg-orange-600 text-white font-black text-sm px-4 py-2 rounded-lg shadow-lg shadow-orange-500/20 transition-all hover:shadow-orange-500/40"
                  data-testid="meet-director-btn"
                >
                  <User className="w-4 h-4 mr-2" />
                  MEET THE DIRECTOR
                </Button>
              </div>
              
              <div className="p-6">
                {/* Featured Video Player - Vertical Format */}
                {selectedVideo && (
                  <div className="mb-6">
                    <div className="flex flex-col md:flex-row gap-6">
                      {/* Video Player */}
                      <div className="relative mx-auto md:mx-0" style={{ width: '280px' }}>
                        <div className="aspect-[9/16] bg-black rounded-lg overflow-hidden border-2 border-gray-800">
                          <iframe
                            src={selectedVideo.video_url}
                            title={selectedVideo.title}
                            className="w-full h-full"
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                            allowFullScreen
                          />
                        </div>
                      </div>
                      
                      {/* Video Info */}
                      <div className="flex-1">
                        <div className="px-2 py-1 bg-orange-500/20 text-orange-500 text-xs font-bold uppercase tracking-wider rounded inline-block mb-3">
                          Featured Briefing
                        </div>
                        <h4 className="text-white font-bold text-lg mb-2">{selectedVideo.title}</h4>
                        <p className="text-gray-400 text-sm mb-4">{selectedVideo.description}</p>
                        {selectedVideo.duration && (
                          <span className="inline-flex items-center gap-1 text-gray-500 text-sm">
                            <Clock className="w-4 h-4" /> {selectedVideo.duration}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {/* Past Briefings */}
                {coach_corner?.past_briefings?.length > 0 && (
                  <div className="border-t border-gray-800 pt-6">
                    <h4 className="text-gray-400 font-bold mb-4 flex items-center gap-2 text-sm uppercase tracking-wider">
                      <Play className="w-4 h-4 text-orange-500" />
                      Past Briefings
                    </h4>
                    <div className="grid gap-3">
                      {coach_corner.past_briefings.map((video) => (
                        <button
                          key={video.id}
                          onClick={() => setSelectedVideo(video)}
                          className={`w-full flex items-center gap-4 p-4 rounded-lg transition-all ${
                            selectedVideo?.id === video.id 
                              ? 'bg-orange-500/10 border border-orange-500' 
                              : 'bg-gray-800/50 border border-gray-800 hover:border-gray-700'
                          }`}
                        >
                          <div className={`w-14 h-20 rounded flex items-center justify-center flex-shrink-0 ${
                            selectedVideo?.id === video.id ? 'bg-orange-500/20' : 'bg-gray-700'
                          }`}>
                            <Play className={`w-5 h-5 ${
                              selectedVideo?.id === video.id ? 'text-orange-500' : 'text-white'
                            }`} />
                          </div>
                          <div className="flex-1 text-left">
                            <h5 className="text-white font-medium text-sm">{video.title}</h5>
                            <p className="text-gray-500 text-xs line-clamp-1 mt-1">{video.description}</p>
                          </div>
                          {video.duration && (
                            <span className="text-gray-600 text-xs">{video.duration}</span>
                          )}
                          <ChevronRight className={`w-4 h-4 ${
                            selectedVideo?.id === video.id ? 'text-orange-500' : 'text-gray-600'
                          }`} />
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {!selectedVideo && !coach_corner?.past_briefings?.length && (
                  <div className="text-center py-12">
                    <Video className="w-16 h-16 text-gray-700 mx-auto mb-4" />
                    <p className="text-gray-500">No coach videos available yet.</p>
                    <p className="text-gray-600 text-sm">Check back soon for new briefings!</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* National Scout Ranking & Sport Major Selection */}
      <div className="container mx-auto px-6 py-8 border-t border-gray-800">
        <div className="grid lg:grid-cols-2 gap-8">
          {/* National Scout Ranking */}
          <div>
            <NationalScoutRanking 
              userScore={student?.academic_power_score || 0}
              userName={student?.name || 'You'}
              userSchool="PRSVR Academy"
            />
          </div>
          
          {/* Sport Major Selection */}
          <div className="space-y-6">
            <SportMajorSelection 
              currentMajor={selectedSportMajor || student?.sport_major}
              onSelectMajor={handleSelectSportMajor}
            />
          </div>
        </div>
      </div>

      {/* Daily Training Roadmap Section */}
      <div className="container mx-auto px-6 py-8 border-t border-gray-800">
        <div className="flex items-center gap-4 mb-6">
          <div className="w-10 h-10 bg-blue-500/20 rounded-lg flex items-center justify-center border border-blue-500/30">
            <BookOpen className="w-5 h-5 text-blue-500" />
          </div>
          <div>
            <h2 className="text-xl font-black text-white">
              DAILY <span className="text-orange-500">REPS</span>
            </h2>
            <p className="text-gray-500 text-sm">Your training assignments • Complete each rep to unlock the next</p>
          </div>
        </div>
        
        <DailyRoadmap user={student} />
      </div>

      {/* Upgrade to Elite Modal */}
      <Dialog open={showUpgradeModal} onOpenChange={setShowUpgradeModal}>
        <DialogContent className="bg-gray-900 border-gray-800 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl font-black text-center flex items-center justify-center gap-2">
              <Crown className="w-6 h-6 text-amber-500" />
              Upgrade to Elite Pivot
            </DialogTitle>
          </DialogHeader>
          
          <div className="py-4 text-center">
            <div className="w-16 h-16 bg-amber-500/20 rounded-full flex items-center justify-center mx-auto mb-4 border-2 border-amber-500">
              <FileCheck className="w-8 h-8 text-amber-500" />
            </div>
            
            <p className="text-gray-400 mb-6">
              <span className="text-amber-400 font-bold">On-Demand Transcripts</span> are exclusive to our Elite Pivot members. 
              Upgrade to unlock full advocacy services.
            </p>
            
            <ul className="text-left space-y-2 mb-6 px-4">
              <li className="flex items-start gap-2 text-sm text-gray-300">
                <span className="text-amber-500">✓</span>
                On-Demand Scholastic Transcripts
              </li>
              <li className="flex items-start gap-2 text-sm text-gray-300">
                <span className="text-amber-500">✓</span>
                Transfer & Transition Assistance
              </li>
              <li className="flex items-start gap-2 text-sm text-gray-300">
                <span className="text-amber-500">✓</span>
                Priority Academic Portfolio
              </li>
              <li className="flex items-start gap-2 text-sm text-gray-300">
                <span className="text-amber-500">✓</span>
                Weekly 1-on-1 Strategy Calls
              </li>
            </ul>
            
            <Button
              onClick={() => {
                setShowUpgradeModal(false);
                navigate('/academy/enroll');
              }}
              className="w-full bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-black font-bold py-6"
            >
              Upgrade to Elite Pivot
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Meet the Director Modal */}
      <Dialog open={showDirectorModal} onOpenChange={setShowDirectorModal}>
        <DialogContent className="bg-gray-900 border-gray-800 text-white max-w-2xl">
          <DialogHeader>
            <DialogTitle className="text-xl font-black text-center flex items-center justify-center gap-2">
              <User className="w-6 h-6 text-orange-500" />
              MEET THE DIRECTOR
            </DialogTitle>
          </DialogHeader>
          
          <div className="py-4">
            {/* YouTube Video Embed */}
            <div className="aspect-video w-full rounded-lg overflow-hidden border-2 border-orange-500/30 mb-6">
              <iframe
                src="https://www.youtube.com/embed/lZ7ZP3WGmKU?rel=0"
                title="Meet Director Brandon Williamson - IXL Partnership"
                className="w-full h-full"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            </div>
            
            {/* Contextual Text */}
            <div className="bg-gray-800/50 border border-gray-700 rounded-lg p-4">
              <p className="text-gray-300 text-sm leading-relaxed">
                <span className="text-orange-500 font-bold">Director Brandon Williamson</span> and his family are featured by IXL for their commitment to the Scholar-Athlete lifestyle. This is the blueprint we are building together.
              </p>
            </div>
            
            {/* Close Button */}
            <Button
              onClick={() => setShowDirectorModal(false)}
              className="w-full mt-6 bg-orange-500 hover:bg-orange-600 text-white font-bold py-4"
            >
              Close
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default HighPerformanceDashboard;
